import { APIInteractionGuildMember, Collection, ColorResolvable, EmbedBuilder, GuildMember, Interaction, LocaleString, Message, PermissionsBitField } from "discord.js";
import { MessageArguments, ICommandManager, SelectMenus, PermissionsMode, RunIn, AnyCommand } from "../typings/commands";
import { Bot } from "..";
import { Emoji } from "../typings/bot";
import { escape } from "@backs/utils"
import { ButtonCommand, ContextMenuCommand, MessageCommand, ModalCommand, SelectMenuCommand, SlashCommand } from "../structures";

export default class CommandManager<Configs extends Record<string, any>, Emojis extends Record<string, Emoji>, Colors extends Record<string, ColorResolvable>> implements ICommandManager {
    public client: Bot<Configs, Emojis, Colors>;

    constructor(client: Bot<Configs, Emojis, Colors>) {
        this.client = client;
    }

    public stores = {
        slash: new Collection<string, SlashCommand>(),
        contextMenuUser: new Collection<string, ContextMenuCommand<"user">>(),
        contextMenuMessage: new Collection<string, ContextMenuCommand<"message">>(),
        message: new Collection<string, MessageCommand<any>>(),
        selectMenu: new Collection<string, SelectMenuCommand<keyof SelectMenus, any>>(),
        button: new Collection<string, ButtonCommand<any>>(),
        modal: new Collection<string, ModalCommand<any>>(),
    };

    public ratelimits = {
        user: new Collection<string, { command: string; type: "message" | "slash" | "contextMenuUser" | "contextMenuMessage"; expires: number; }[]>(),
        guild: new Collection<string, { command: string; type: "message" | "slash" | "contextMenuUser" | "contextMenuMessage"; expires: number; }[]>(),
        channel: new Collection<string, { command: string; type: "message" | "slash" | "contextMenuUser" | "contextMenuMessage"; expires: number; }[]>(),
        global: new Collection<string, { command: string; type: "message" | "slash" | "contextMenuUser" | "contextMenuMessage"; expires: number; }[]>(),
    };

    destroy() {
        for (const store of Object.values(this.stores)) store.clear();
        for (const ratelimit of Object.values(this.ratelimits)) ratelimit.clear();
    }

    async run(intOrMsg: Interaction<"cached"> | Message, { commandName, precommand, postcommand, postprocess, postrun, behavior, translations }: {
        commandName?: string,
        precommand?: (intOrMsg: Interaction<"cached"> | Message) => boolean | Promise<boolean>,
        postcommand?: (intOrMsg: Interaction<"cached"> | Message, command: AnyCommand) => boolean | Promise<boolean>,
        postprocess?: (intOrMsg: Interaction<"cached"> | Message, command: AnyCommand) => boolean | Promise<boolean>,
        postrun?: (intOrMsg: Interaction<"cached"> | Message, command: AnyCommand, result: any) => boolean | Promise<boolean>,
        behavior?: "preferIgnore" | "preferReply" | "auto",
        translations?: Partial<Record<"notallowed" | "ratelimit" | "unavailable", Partial<Record<LocaleString, string>>>>;
    }) {
        try {
            if ((await precommand?.(intOrMsg)) === false) return;
    
            if (intOrMsg instanceof Message) {
                const command = this.stores.message.get(commandName!);
                if (!command) {
                    if (behavior === "preferReply") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.unavailable?.[(intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ]
                    });
                    else return;
                }
    
                if ((await postcommand?.(intOrMsg, command)) === false) return;
                
                if (command.enabled === false) {
                    if (behavior === "preferReply") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.unavailable?.[(intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ]
                    });
                    else return;     
                }
    
                if (
                    (intOrMsg.inGuild() && command.options?.runIn === RunIn.DM) ||
                    (!intOrMsg.inGuild() && command.options?.runIn === RunIn.Guild) ||
                    (intOrMsg.inGuild() && !this.checkPermissions(command, intOrMsg.member!))
                ) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.notallowed?.[(intOrMsg.guild?.preferredLocale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "You cannot use this command.")
                            .setColor("Red")
                        ]
                    })
                    else return;
                }
    
                if (this.ratelimit(command, intOrMsg.author.id)) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.ratelimit?.[intOrMsg.guild?.preferredLocale ?? "en_US"] ?? "You're a bit too quick there.")
                            .setColor("Red")
                        ]
                    })
                    else return;
                }
    
                const args = command.data.arguments ? this.parseArgs(intOrMsg.content.slice(intOrMsg.content.indexOf(commandName!) - 1 + commandName!.length), command.data.arguments) : {};
    
                if ((await postprocess?.(intOrMsg, command)) === false) return;
    
                const result = await command.run(intOrMsg, args); 
    
                if ((await postrun?.(intOrMsg, command, result)) === false) return;
            } else if (intOrMsg.isCommand()) {
                let command: SlashCommand | ContextMenuCommand<"user"> | ContextMenuCommand<"message"> | undefined;
                intOrMsg.member
                if (intOrMsg.isChatInputCommand()) command = this.stores.slash.get(intOrMsg.commandName);
                else {
                    if (intOrMsg.isUserContextMenuCommand()) command = this.stores.contextMenuUser.get(intOrMsg.commandName);
                    else command = this.stores.contextMenuMessage.get(intOrMsg.commandName)
                }
    
                if (!command) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.unavailable?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return;
                }
    
                if ((await postcommand?.(intOrMsg, command)) === false) return;
    
                if (command.enabled === false) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                               .setDescription(translations?.unavailable?.[(intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    });
                    else return;     
                }
    
                if (
                    (intOrMsg.inGuild() && command.options?.runIn === RunIn.DM) ||
                    (!intOrMsg.inGuild() && command.options?.runIn === RunIn.Guild) ||
                    (intOrMsg.inGuild() && !this.checkPermissions(command, intOrMsg.member))
                ) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.notallowed?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "You cannot use this command.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return;
                }
    
                if (this.ratelimit(command, intOrMsg.user.id)) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.ratelimit?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "You're a bit too quick there.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return;
                }
    
                if ((await postprocess?.(intOrMsg, command)) === false) return;
    
                const result = await (command.run as (interaction: Interaction) => any | Promise<any>)(intOrMsg)
    
                if ((await postrun?.(intOrMsg, command, result)) === false) return;
            } else if (intOrMsg.isAutocomplete()) {
                const command = this.stores.slash.get(intOrMsg.commandName);
                if (!command || command.enabled === false || !(intOrMsg.options.getFocused(true).name in (command.autocomplete ?? {}))) return await intOrMsg.respond([]);
    
                if ((await postcommand?.(intOrMsg, command)) === false) return;
    
                const result = await command.autocomplete![intOrMsg.options.getFocused(true).name](intOrMsg, intOrMsg.options.getFocused());
                
                if ((await postrun?.(intOrMsg, command, result)) === false) return;
            } else {
                let command: SelectMenuCommand<any, any> | ButtonCommand<any> | ModalCommand<any> | undefined;
                const args = this.parseArgs(intOrMsg.customId, command?.arguments ?? []);
    
                if (!args || !args.id) {
                    if (behavior !== "preferIgnore" || intOrMsg.isModalSubmit()) return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.unavailable?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return await intOrMsg.update({});
                }

                if (intOrMsg.isButton() && args.id === "multipageembed") return;
    
                if (intOrMsg.isButton()) command = this.stores.button.get(args.id);
                else if (intOrMsg.isAnySelectMenu()) command = this.stores.selectMenu.get(args.id);
                else command = this.stores.modal.get(args.id);
    
                if (!command) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.unavailable?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return;
                }
                
                if ((await postcommand?.(intOrMsg, command)) === false) return;
    
                if (command.enabled === false) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                               .setDescription(translations?.unavailable?.[(intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "This command is unavailable.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    });
                    else return;     
                }
                
                if (args.userId && intOrMsg.user.id !== args.userId) {
                    if (behavior !== "preferIgnore") return await intOrMsg.reply({
                        embeds: [
                            new EmbedBuilder()
                            .setDescription(translations?.notallowed?.[(intOrMsg.locale ?? intOrMsg.guild?.preferredLocale) ?? "en_US"] ?? "You are not allowed to use this command.")
                            .setColor("Red")
                        ],
                        ephemeral: true
                    })
                    else return;
                }
    
                if ((await postprocess?.(intOrMsg, command)) === false) return;
    
                const result = await command.run(intOrMsg, args);
    
                if ((await postrun?.(intOrMsg, command, result)) === false) return;
            }
        }  catch (err) {
            this.client.logger.error(err);
            if (intOrMsg instanceof Message) await intOrMsg.reply({
                embeds: [
                    new EmbedBuilder()
                    .setDescription("An error occurred while executing this command.")
                    .setColor(this.client.color("error"))
                ]
            });
            else {
                if (intOrMsg.isAutocomplete()) {
                    if (!intOrMsg.responded) await intOrMsg.respond([]).catch(() => {});
                } else if (intOrMsg.replied) await intOrMsg.followUp({
                    embeds: [
                        new EmbedBuilder()
                        .setDescription("An error occurred while executing this command.")
                        .setColor(this.client.color("error"))
                    ]
                }).catch(() => {});
                else if (intOrMsg.deferred) await intOrMsg.editReply({
                    embeds: [
                        new EmbedBuilder()
                        .setDescription("An error occurred while executing this command.")
                        .setColor(this.client.color("error"))
                    ]
                }).catch(() => {});
                else await intOrMsg.reply({
                    embeds: [
                        new EmbedBuilder()
                        .setDescription("An error occurred while executing this command.")
                        .setColor(this.client.color("error"))
                    ],
                    ephemeral: true
                }).catch(() => {});
            }
        }
    }

    checkPermissions(command: MessageCommand<any> | SlashCommand | ContextMenuCommand<"user" | "message">, user: GuildMember | APIInteractionGuildMember) {
        if (this.client.botOptions.owners?.includes(user.user.id)) return true;

        const permissions = user instanceof GuildMember ? user.permissions : new PermissionsBitField(BigInt(user.permissions));
        const restricted = command.options?.restricted ?? false;
        if (!command.options?.permissions) return !restricted;

        if (command.options.permissions.allow) {
            const mode = command.options.permissions.allowMode ?? PermissionsMode.MatchOne;
            
            if (mode === PermissionsMode.MatchOne) {
                for (const permission of command.options.permissions.allow)
                    if (permissions.has(permission, true)) return true;

                return false;
            } else if (mode === PermissionsMode.MatchAll) {
                for (const permission of command.options.permissions.allow)
                    if (!permissions.has(permission, true)) return false;

                return true;
            } else {
                for (const permission of command.options.permissions.allow)
                    if (permissions.has(permission, true)) return false;

                return true;
            }
        }

        if (command.options.permissions.deny) {
            const mode = command.options.permissions.allowMode ?? PermissionsMode.MatchOne;
            
            if (mode === PermissionsMode.MatchOne) {
                for (const permission of command.options.permissions.deny)
                    if (permissions.has(permission, true)) return false;

                return true;
            } else if (mode === PermissionsMode.MatchAll) {
                for (const permission of command.options.permissions.deny)
                    if (!permissions.has(permission, true)) return true;

                return false;
            } else {
                for (const permission of command.options.permissions.deny)
                    if (permissions.has(permission, true)) return true;

                return false;
            }
        }

        return !restricted;
    }

    ratelimit(command: MessageCommand<any> | SlashCommand | ContextMenuCommand<"user" | "message">, userId: string) {
        const ratelimit = command.options?.ratelimit
        if (!ratelimit) return false;

        for (const [key, value] of Object.entries(ratelimit)) {
            if (value === 0) continue;
            const now = Date.now();
            const ratelimits = this.ratelimits[key as keyof typeof this.ratelimits];
            
            if (ratelimits.has(userId)) {
                const userRatelimits = ratelimits.get(userId)!;
                ratelimits.set(userId, userRatelimits.filter(r => r.expires > now));
                const commandRatelimit = userRatelimits.find(r => r.command === command.data.name && r.type === command.commandType);
                if (commandRatelimit && commandRatelimit.expires > now) return true;
            }
            
            const expires = now + value;
            ratelimits.set(userId, [...(ratelimits.get(userId) ?? []), { command: command.data.name, type: command.commandType, expires }]);
        }

        return false;
    }

    parseArgs<T extends string>(args: string, format: T): MessageArguments<T>;
    parseArgs<T extends string[]>(args: string, format: T): { [K in T[number]]?: string } &  { id: string; userId?: string };
    parseArgs<T extends string | string[]>(args: string, format: T) {
        if (Array.isArray(format)) {
            const argsArray = args.split("/");

            return {
                id: argsArray.shift(),
                userId: argsArray.shift(),
                ...argsArray.reduce((o, v, i) => { o[format[i]] = v; return o; }, {} as  { [Key in T[number]]?: string })
            } as  { [Key in T[number]]?: string } & { id: string; userId?: string };
        } else if (typeof format === "string") {
            const argDefinitions = args.match(/<[^<>\[\]]>|\[[^<>\[\]]\]>/g) ?? [],
                parsedArgs: Record<string, string | boolean | undefined> = {},
                content = args.split(/\s+/);

            const getNext = (required: boolean) => {
                const c = content.shift();
                if (required && !c) throw new Error("Missing required argument.");
                else if (!c) return undefined;
                else return c;
            }
            
            for (let i = 0; i < argDefinitions.length; i++) {
                const arg = argDefinitions[i];
                const [name, options] = arg.match(/(?:<|\[)([a-zA-Z0-9_-]+)(:(?:[a-zA-Z0-9_-],? *)+|\((?:.+)\)|\.\.\.)?(?:>|])/) ?? [];
                const isRequired = arg.startsWith("<") && arg.endsWith(">");

                if (!isRequired && (!arg.startsWith("[") || !arg.endsWith("]"))) throw new Error("Invalid format.");
                if (!name) throw new Error("Invalid format.");

                if (options) {
                    if (options.endsWith("...")) {
                        if (i !== (argDefinitions.length - 1)) throw new Error("Rest arguments must be the last argument.");
                        if (content.length === 0) {
                            if (isRequired) throw new Error("Missing required argument.");
                            else continue;
                        }

                        const c = args.match(new RegExp(`\\s(${escape(content[0])}\s+)`))![1];
                        parsedArgs[name] = c;
                    }
                    else if (options.startsWith("(") && options.endsWith(")")) {
                        const regex = new RegExp(options.slice(1, -1));
                        const c = getNext(isRequired);
                        if (c && !regex.test(c)) {
                            if (isRequired) throw new Error("Invalid required argument.");
                            else {
                                parsedArgs[name] = undefined;
                                continue;
                            };
                        }
                        parsedArgs[name] = c;
                    }
                    else if (options.startsWith(":")) {
                        const enums = options.slice(1).split(",");
                        const c = getNext(isRequired);
                        if (c && !enums.includes(c)) {
                            if (isRequired) throw new Error("Invalid required argument.");
                            else {
                                parsedArgs[name] = enums.length === 1 ? false : undefined;
                                continue;
                            }
                        }
                        parsedArgs[name] = enums.length === 1 ? true : c;
                    } else throw new Error("Invalid format.");
                } else parsedArgs[name] = getNext(isRequired);
            }

            return parsedArgs as MessageArguments<T & string>
        } else throw new Error("Invalid format.");
    }
}